import { format } from 'date-fns';
import { usePostHog } from 'posthog-js/react';
import { FC } from 'react';

import { ImagesApp } from '../../../constants/images';
import { useBreadCrumbsContext } from '../../../hooks/contexts/breadCrumb.context';
import { useCartContext } from '../../../hooks/contexts/cart.context';
import { ComboData } from '../../../hooks/contexts/comboProgress.context';
import { useEventContext } from '../../../hooks/contexts/event.context';
import { useI18nContext } from '../../../hooks/contexts/i18n.context';
import useFormattedDateTimeZone from '../../../hooks/useFormattedDateTimeZone';
import useRules from '../../../hooks/useRules';
import { ComboInCart } from '../../../types/cart';
import { TicketType } from '../../../types/ticket';
import formatNumericValue from '../../../utils/formatNumericValue';
import Popover from '../../Popover';
import { TicketCart } from './SelectedTickets';
import * as Styles from './summary.styles';

type SelectedTicketProps = {
  ticket?: TicketCart;
  combo?: ComboInCart;
  isCombo?: boolean;
  index: number;
}

const SelectedTicket: FC<SelectedTicketProps> = ({
  ticket,
  index,
  combo,
  isCombo = false,
}) => {
  const posthog = usePostHog();
  const { thisTicketHasAnError } = useRules();
  const { errorValidating, removeSelectedTickets, updateComboInCart } = useCartContext();
  const { event } = useEventContext();
  const { errorsLimit, currencyFormat } = useEventContext();
  const { translate, currentLocale : { locale }, supportedLocales } = useI18nContext();
  const { formatDate } = useFormattedDateTimeZone();
  const { isNamingPage } = useBreadCrumbsContext();

  const hasError = thisTicketHasAnError({ ticket : ticket as unknown as TicketType, error : errorValidating });
  const errorLimit = errorsLimit.find((error) => error.id === ticket?.id);

  const formattedDate = (ticket: TicketCart) => {
    const hourFormat = locale === 'en-US' ? 'hh\':\'mm a' : 'HH\':\'mm';
    const dateFormat = locale === 'en-US' ? 'EEEE, MMMM dd' : 'EEEE, dd \'de\' MMMM';
    const adjustedDate = formatDate(ticket.date, event?.timezone ?? 'America/Sao_Paulo');

    return format(
      adjustedDate,
      `${dateFormat} ' - ' ${hourFormat}`,
      { locale : supportedLocales({ lang : locale }) },
    ).toLowerCase();
  };

  const handleComboTotalValue = (combo: ComboInCart, withDiscount = false) => {
    if (withDiscount) {
      const totalPerCombo = combo.comboDates.reduce((acc, date) => {
        const value = date.groups[0].tickets[0].price;
        return acc + (value - (value * date.discount) / 100);
      }, 0);

      return totalPerCombo * combo.quantity;
    }

    return combo.comboDates.reduce((acc, date) => acc + date.groups[0].tickets[0].price, 0);
  };

  const totalSessionsInCombo = (): string => {
    const totalSessions = Number(combo?.comboDates.length);

    if (totalSessions > 1)
      return translate(['COMBO', 'SUMMARY', 'SESSIONS'], { totalSessions : Number(totalSessions) });

    return translate(['COMBO', 'SUMMARY', 'SESSION'], { totalSessions : Number(totalSessions) });
  };

  return (
    <Styles.Ticket
      $hasError={hasError || Boolean(errorLimit?.error)}
      id={`ticket-container_${index}`}
    >
      <div
        className='quantity'
        id={`ticket-quantity_${index}`}
      >
        <span>{isCombo ? combo?.quantity : ticket?.quantity}x</span>
      </div>
      <div className='details' id={`ticket-details_${index}`}>
        <span className='title' id={`ticket-group_${index}`}>
          {isCombo ? combo?.name : ticket?.group}
        </span>
        <span className='sub-title' id={`ticket-title_${index}`}>
          {isCombo ? (
            totalSessionsInCombo()
          ) : ticket?.title}
        </span>
        <span className='value' id={`ticket-price_${index}`}>
          {formatNumericValue({
            value : isCombo ? (
              handleComboTotalValue(combo as ComboInCart)
            ) : Number(ticket?.price),
            discount : 0,
            currencyFormat,
          })}
        </span>
        <Popover
          render={hasError  || Boolean(errorLimit?.error)}
          id="summary-popover-info"
          text={errorValidating.error || errorLimit?.error || ''}
        >
          <span className='date' id={`ticket-date_${index}`}>
            {isCombo ? (
              translate(['COMBO', 'SUMMARY', 'COMBO_MESSAGE'])
            ) : formattedDate(ticket as TicketCart)} {hasError || Boolean(errorLimit?.error) && (
              <img src={ImagesApp.svg.warningTicket} alt="" />
            )}
          </span>
        </Popover>
      </div>
      <div className='total' id={`ticket-total_${index}`}>
        <span className='value' id={`ticket-total-value_${index}`}>
          {formatNumericValue({
            value : isCombo ? (
              handleComboTotalValue(combo as ComboInCart, true)
            ) : Number(ticket?.accumulatedValueInCart),
            discount : 0,
            currencyFormat,
          })}
        </span>
        {isNamingPage || (
          <button
            onClick={() => {
              if (isCombo) return updateComboInCart(combo as ComboData, true);
              removeSelectedTickets({
                ticketId : Number(ticket?.id),
                definitiveSessionId : ticket?.definitiveSessionId ?? '',
              });
              posthog?.capture('purchase:ticket_selection_ticket_cart_remove');
            }}
            className='text-link'
            id={`ticket-remove-button_${index}`}
          >
            {translate(['INFORMATION', 'REMOVE'])}
          </button>
        )}
      </div>
    </Styles.Ticket>
  );
};

export default SelectedTicket;
